import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ContextInfo {
  companion: {
    name: string;
    personality: string | null;
    responseLength: string;
  };
  systemPrompt: {
    totalLength: number;
    estimatedTokens: number;
  };
  codexes: {
    note: string;
  };
  memories: {
    count: number;
    items: { content: string }[];
  };
  summaries: {
    count: number;
  };
  recentMessages: {
    count: number;
  };
  user: {
    name: string | null;
    timezone: string;
  };
}

interface ContextInfoPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
}

const ContextInfoPanel: React.FC<ContextInfoPanelProps> = ({ isOpen, onClose, conversationId }) => {
  const { t } = useTranslation();
  const [context, setContext] = useState<ContextInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && conversationId) {
      loadContext();
    }
  }, [isOpen, conversationId]);

  const loadContext = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/chat/conversations/${conversationId}/context`);
      setContext(res.data);
    } catch (err) {
      console.error('Failed to load context:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div
        className="fixed right-0 top-0 h-full w-80 max-w-[85vw] z-50 flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #1a1625 0%, #151221 100%)',
          borderLeft: '1px solid rgba(139, 92, 246, 0.15)',
          boxShadow: '-4px 0 32px rgba(0,0,0,0.6)',
        }}
      >
        <div className="flex items-center justify-between p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text">
            {t('context.title', 'Active Context')}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-dark-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : context ? (
            <>
              {/* System Prompt */}
              <Section title={t('context.systemPrompt', 'System Prompt')} emoji="🧠">
                <InfoRow
                  label={t('context.totalSize', 'Total size')}
                  value={`~${context.systemPrompt.estimatedTokens.toLocaleString()} tokens`}
                />
                <InfoRow
                  label={t('context.responseLength', 'Response length')}
                  value={context.companion.responseLength}
                />
              </Section>

              {/* Personality */}
              {context.companion.personality && (
                <Section title={t('context.personalityText', 'Personality Text')} emoji="✨">
                  <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary whitespace-pre-wrap">
                    {context.companion.personality}
                  </p>
                </Section>
              )}

              {/* Memories */}
              <Section title={t('context.memories', 'Memories')} emoji="💭">
                <InfoRow
                  label={t('context.activeMemories', 'Active memories')}
                  value={String(context.memories.count)}
                />
                {context.memories.items.map((m, i) => (
                  <p
                    key={i}
                    className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-1 pl-2 border-l-2 border-primary-500/30"
                  >
                    {m.content}
                  </p>
                ))}
              </Section>

              {/* Summaries */}
              <Section title={t('context.summaries', 'Summaries')} emoji="📝">
                <InfoRow
                  label={t('context.activeSummaries', 'Active summaries')}
                  value={String(context.summaries.count)}
                />
              </Section>

              {/* Recent Messages */}
              <Section title={t('context.recentMessages', 'Recent Messages')} emoji="💬">
                <InfoRow
                  label={t('context.inContext', 'In context window')}
                  value={String(context.recentMessages.count)}
                />
              </Section>

              {/* User Info */}
              <Section title={t('context.userInfo', 'User Info')} emoji="👤">
                {context.user.name && (
                  <InfoRow label={t('context.name', 'Name')} value={context.user.name} />
                )}
                <InfoRow label={t('context.timezone', 'Timezone')} value={context.user.timezone} />
              </Section>
            </>
          ) : (
            <p className="text-sm text-dark-text-secondary text-center py-8">
              {t('context.loadFailed', 'Failed to load context info.')}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const Section: React.FC<{
  title: string;
  emoji: string;
  children: React.ReactNode;
}> = ({ title, emoji, children }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase text-primary-400 mb-2 tracking-wider">
      {emoji} {title}
    </h3>
    <div
      className="rounded-xl p-[1px]"
      style={{
        background: 'linear-gradient(135deg, rgba(168,85,247,0.9), rgba(56,189,248,0.4))',
      }}
    >
      <div
        className="rounded-[0.7rem] p-3 space-y-1.5"
        style={{
          background: 'rgba(15,23,42,0.96)',
          boxShadow: '0 18px 40px rgba(15,23,42,0.75)',
          backdropFilter: 'blur(18px)',
        }}
      >
        {children}
      </div>
    </div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between text-xs">
    <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
      {label}
    </span>
    <span className="text-dark-text dark:text-dark-text text-light-text font-medium">{value}</span>
  </div>
);

export default ContextInfoPanel;
